import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getAllTasksByProjectId } from '../../../../../services/taskService';
import ProtectedComponent from '../../../../../components/protected/ProtectedComponent';
import Iconify from '../../../../../components/iconify';
import { useProject } from '../../../Layout/header/context';
import formatDate from '../../../../../utils/formatDate';
import { isAdmin, isStakeholder } from '../../../../../utils/share';

const TopToolBar = ({ columnFilters, globalFilter, onCreateClick, sorting }) => {
  const { id } = useParams();
  const { projectDetails, projectRole, isEditPermission } = useProject();

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: 'tasks.csv', // Add .csv extension
  });

  // Handling CSV export
  const handleExportData = () => {
    // Fetch all matching data without limiting the export to the current page.
    const criteria = {
      projectId: id,
      pageSize: 9999,
      filters: JSON.stringify(columnFilters ?? []),
      wantCompletedTask:true,
      globalFilter,
      sorting: JSON.stringify(sorting ?? []),
    };

    getAllTasksByProjectId(criteria)
      .then((allTasks) => {
        // Mapping task list to a flat structure for CSV export
        const flattenTaskList = allTasks?.data?.rows?.map((task) => ({
          Name: task?.taskName,
          Description: task?.description || '',
          Project_Name: task?.projects?.projectName,
          Task_Type: task?.taskType,
          Assigned_To: task?.user?.email,
          Start_Date: formatDate(task?.startDate),
          End_Date: formatDate(task?.endDate),
          Section: task?.section?.name,
          Status: task?.status,
          Planned_Hours: task?.plannedHours,
          Client_Name: task?.projects?.clients?.clientName,
        }));

        const csv = generateCsv(csvConfig)(flattenTaskList);
        download(csvConfig)(csv);
      })
      .catch(() => {
        toast.error('An error occurred while exporting the data. Please try again later.');
      });
  };

  const createTaskDisabled = projectDetails?.allowManualTask === false && !isEditPermission;

  return (
    <Grid container justifyContent="space-between">
      <Grid item xs={5} alignSelf="center">
        <Grid container>
          <Grid item xs={1} marginTop="5px">
            <ChecklistIcon />
          </Grid>
          <Grid item xs={6} alignSelf="center">
            <Typography variant="h5">Task List</Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        <ProtectedComponent permission={'CREATE_TASK'}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={handleExportData}>
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
          {(isAdmin() || isStakeholder() || isEditPermission || projectDetails?.allowManualTask || projectRole !== 'Team Members') && (
            <Tooltip title={isStakeholder() ? "Add Issue" : "Add Task"}>
              <IconButton onClick={onCreateClick} disabled={createTaskDisabled}>
                <Iconify icon="eva:plus-fill" />
              </IconButton>
            </Tooltip>
          )}
        </ProtectedComponent>
      </Grid>
    </Grid>
  );
};

TopToolBar.propTypes = {
  columnFilters: PropTypes.array,
  globalFilter: PropTypes.string,
  onCreateClick: PropTypes.func.isRequired,
  sorting: PropTypes.array,
};

TopToolBar.defaultProps = {
  columnFilters: [],
  globalFilter: '',
  sorting: [],
};

export default TopToolBar;
